import React from 'react'
import { Button, Modal, Popconfirm, Select, Space, Table, Tag, Typography } from 'antd'
import { ColumnsType } from 'antd/es/table'
import moment from 'moment'
import { withdrawalRequestsApi } from 'api/withdrawalRequests'
import {
  EWithdrawalRequestStatus,
  IWithdrawalRequest,
  IWithdrawalRequestSeller
} from 'models/withdrawalRequest'
import { useIsMobile } from 'shared/hooks/useIsMobile'
import { useAppDispatch } from 'shared/hooks/useReduxHooks'
import { getNormalizedPrice } from 'shared/utils/getNormalizedPrice'
import { showServerError } from 'shared/utils/showServerError'
import { showMessage } from 'shared/utils/showMessage'
import { downloadBlobResponse } from 'shared/utils/downloadBlobResponse'
import { SectionTitle } from 'components/ui'
import { CardLink } from 'components/ui/CardLink/CardLink'
import {
  WithdrawalRequestDetails,
  renderWithdrawalPeriod
} from 'components/transactions/WithdrawalRequestDetails/WithdrawalRequestDetails'
import { getSellerEditPath } from 'routing/utils/getSellerEditPath'
import { getPendingWithdrawalRequests } from 'store/withdrawalRequests'
import {
  getWithdrawalRequestStatusColor,
  getWithdrawalRequestStatusLabel
} from 'shared/constants/withdrawals'
import styles from './WithdrawalRequests.module.scss'

const PAGE_SIZE = 20

const getSellerTitle = (request: IWithdrawalRequest) => {
  const seller = request.seller || request.seller_id

  if (typeof seller === 'string' || !seller) {
    return request.shop_name || seller || '—'
  }

  return getSellerName(seller)
}

const getSellerName = (seller: IWithdrawalRequestSeller) => {
  return (
    seller.shop_name ||
    seller.full_name ||
    [seller.last_name, seller.first_name].filter(Boolean).join(' ') ||
    seller.name ||
    seller._id ||
    '—'
  )
}

const renderSellerTitle = (request: IWithdrawalRequest, newTab?: boolean) => {
  const seller = request.seller || request.seller_id
  const sellerId = typeof seller === 'string' ? seller : seller?._id
  const title = getSellerTitle(request)

  return sellerId ? (
    <CardLink to={getSellerEditPath(sellerId)} newTab={newTab}>
      {title}
    </CardLink>
  ) : (
    title
  )
}

// Выплаченная заявка не дополняется, поэтому подачи до конца периода уходят в следующий
const isPeriodRunning = ({ period_end }: IWithdrawalRequest) => {
  return !!period_end && moment().format('YYYY-MM-DD') <= period_end
}

export const WithdrawalRequests: React.FC = () => {
  const isMobile = useIsMobile()
  const dispatch = useAppDispatch()
  const [requests, setRequests] = React.useState<IWithdrawalRequest[]>([])
  const [currentPage, setCurrentPage] = React.useState(1)
  const [total, setTotal] = React.useState(0)
  const [status, setStatus] = React.useState<EWithdrawalRequestStatus | undefined>()
  const [isLoading, setIsLoading] = React.useState(false)
  const [selectedRequest, setSelectedRequest] = React.useState<IWithdrawalRequest | null>(null)
  const [isDetailsLoading, setIsDetailsLoading] = React.useState(false)
  const [isStatusLoading, setIsStatusLoading] = React.useState(false)
  const [isResendLoading, setIsResendLoading] = React.useState(false)
  const [isCardLoading, setIsCardLoading] = React.useState(false)

  const getRequests = React.useCallback(async (skip: number) => {
    try {
      setIsLoading(true)
      const response = await withdrawalRequestsApi.getList({
        skip: String(skip),
        limit: String(PAGE_SIZE),
        status
      })
      setRequests(response.data.requests)
      setTotal(response.data.count)
    } catch (e) {
      showServerError(e)
    } finally {
      setIsLoading(false)
    }
  }, [status])

  React.useEffect(() => {
    getRequests(0)
    setCurrentPage(1)
  }, [getRequests])

  const openDetails = async (request: IWithdrawalRequest) => {
    try {
      setIsDetailsLoading(true)
      setSelectedRequest(request)
      const response = await withdrawalRequestsApi.getById(request._id)
      setSelectedRequest(response.data)
    } catch (e) {
      showServerError(e)
    } finally {
      setIsDetailsLoading(false)
    }
  }

  const closeDetails = () => {
    setSelectedRequest(null)
  }

  const resendEmail = async () => {
    if (!selectedRequest) {
      return
    }

    try {
      setIsResendLoading(true)
      await withdrawalRequestsApi.resendEmail(selectedRequest._id)
      showMessage('success', 'Письмо отправлено', '')
    } catch (e) {
      showServerError(e)
    } finally {
      setIsResendLoading(false)
    }
  }

  const downloadCard = async () => {
    if (!selectedRequest) {
      return
    }

    try {
      setIsCardLoading(true)
      const response = await withdrawalRequestsApi.downloadCard(selectedRequest._id)
      downloadBlobResponse(response, `Заявка на вывод № ${selectedRequest.number}.pdf`)
    } catch (e) {
      showServerError(e)
    } finally {
      setIsCardLoading(false)
    }
  }

  const completeRequest = async () => {
    if (!selectedRequest) {
      return
    }

    try {
      setIsStatusLoading(true)
      const response = await withdrawalRequestsApi.updateStatus(
        selectedRequest._id,
        { status: EWithdrawalRequestStatus.COMPLETED }
      )
      setSelectedRequest(response.data)
      showMessage('success', 'Заявка переведена в статус "Выведено"', '')
      getRequests((currentPage - 1) * PAGE_SIZE)
      dispatch(getPendingWithdrawalRequests())
    } catch (e) {
      showServerError(e)
    } finally {
      setIsStatusLoading(false)
    }
  }

  const columns: ColumnsType<IWithdrawalRequest> = [
    {
      title: 'Номер заявки',
      dataIndex: 'number',
      render: (_, request) => (
        <Typography.Text>{request.number ?? request._id}</Typography.Text>
      )
    },
    {
      title: 'Расчётный период',
      dataIndex: 'period_start',
      render: (_, request) => (
        <Typography.Text className={styles.nowrap}>
          {renderWithdrawalPeriod(request)}
        </Typography.Text>
      )
    },
    {
      title: 'Продавец / магазин',
      dataIndex: 'seller_id',
      render: (_, request) => (
        <Typography.Text>{renderSellerTitle(request)}</Typography.Text>
      )
    },
    {
      title: 'Сумма',
      dataIndex: 'sum',
      render: (_, { sum }) => (
        <Typography.Text className={styles.nowrap}>
          {getNormalizedPrice(sum)}
        </Typography.Text>
      )
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      render: (_, { status }) => (
        <Tag color={getWithdrawalRequestStatusColor(status)}>
          {getWithdrawalRequestStatusLabel(status)}
        </Tag>
      )
    }
  ]

  // На телефоне пять колонок собраны в две: заявка с периодом и продавцом, сумма со статусом
  const mobileColumns: ColumnsType<IWithdrawalRequest> = [
    {
      title: 'Заявка',
      dataIndex: 'number',
      width: '55%',
      render: (_, request) => (
        <div className={styles.mobileCell}>
          <Typography.Text className={styles.mobileNumber}>
            {request.number ?? request._id}
          </Typography.Text>
          <Typography.Text type='secondary' className={styles.mobileSubText}>
            {renderWithdrawalPeriod(request)}
          </Typography.Text>
          <Typography.Text className={styles.mobileSubText}>
            {renderSellerTitle(request)}
          </Typography.Text>
        </div>
      )
    },
    {
      title: 'Сумма и статус',
      dataIndex: 'sum',
      width: '45%',
      render: (_, { sum, status }) => (
        <div className={styles.mobileCell}>
          <Typography.Text>{getNormalizedPrice(sum)}</Typography.Text>
          <Tag color={getWithdrawalRequestStatusColor(status)}>
            {getWithdrawalRequestStatusLabel(status)}
          </Tag>
        </div>
      )
    }
  ]

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <SectionTitle level={2} style={{ margin: 0 }}>
          Заявки на вывод
        </SectionTitle>
        <Select
          allowClear
          placeholder='Статус'
          value={status}
          style={{ minWidth: 220 }}
          className={styles.statusFilter}
          onChange={setStatus}
          options={Object.values(EWithdrawalRequestStatus).map((item) => ({
            value: item,
            label: getWithdrawalRequestStatusLabel(item)
          }))}
        />
      </div>
      <Table
        columns={isMobile ? mobileColumns : columns}
        size={isMobile ? 'small' : undefined}
        tableLayout={isMobile ? 'fixed' : undefined}
        dataSource={requests}
        loading={isLoading}
        rowKey={(request) => request._id}
        onRow={(request) => ({
          onClick: () => openDetails(request)
        })}
        pagination={{
          current: currentPage,
          onChange: (page) => {
            setCurrentPage(page)
            getRequests((page - 1) * PAGE_SIZE)
          },
          pageSize: PAGE_SIZE,
          total,
          showSizeChanger: false
        }}
      />
      <Modal
        centered
        open={Boolean(selectedRequest)}
        footer={null}
        onCancel={closeDetails}
        width={860}
      >
        {selectedRequest && (
          <>
            <WithdrawalRequestDetails
              request={selectedRequest}
              isLoading={isDetailsLoading}
              seller={renderSellerTitle(selectedRequest, true)}
            />
            <div className={styles.modalActions}>
              <Space wrap>
                <Button
                  loading={isResendLoading}
                  onClick={resendEmail}
                >
                  Отправить на Email
                </Button>
                <Button
                  loading={isCardLoading}
                  onClick={downloadCard}
                >
                  Скачать карточку
                </Button>
              </Space>
              <Space>
                <Button onClick={closeDetails}>Закрыть</Button>
                {selectedRequest.status !== EWithdrawalRequestStatus.COMPLETED && (
                  <Popconfirm
                    title='Подтвердить вывод?'
                    description={
                      isPeriodRunning(selectedRequest)
                        ? 'Расчётный период ещё идёт — новые заявки продавца попадут в заявку следующего периода'
                        : undefined
                    }
                    okText='OK'
                    cancelText='Отмена'
                    onConfirm={completeRequest}
                  >
                    <Button
                      type='primary'
                      loading={isStatusLoading}
                    >
                      Подтвердить вывод
                    </Button>
                  </Popconfirm>
                )}
              </Space>
            </div>
          </>
        )}
      </Modal>
    </div>
  )
}
